import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ApiListing } from "@/data/mockData";

type Props = {
  listing: ApiListing;
  index?: number;
};

type CancelTag = "flexivel" | "moderado" | "rigido" | "desconhecido";
type ImageFrame = { x: number; y: number };

function normalizeCancelTag(listing: ApiListing): CancelTag {
  const raw =
    (listing as any)?.cancelamento?.tag ??
    (listing as any)?.cancelamento?.key ??
    (listing as any)?.cancelamento?.label ??
    "";

  const s = String(raw).toLowerCase();
  if (s.includes("flex")) return "flexivel";
  if (s.includes("mod") || s.includes("inter")) return "moderado";
  if (s.includes("rig")) return "rigido";
  return "desconhecido";
}

function badgeClassByTag(tag: CancelTag) {
  switch (tag) {
    case "flexivel":
      return "bg-emerald-100 text-emerald-800";
    case "moderado":
      return "bg-amber-100 text-amber-800";
    case "rigido":
      return "bg-red-100 text-red-800";
    default:
      return "bg-muted text-foreground";
  }
}

function pickCancelamento(listing: ApiListing) {
  const c: any = (listing as any)?.cancelamento ?? {};

  const badge: string | null =
    typeof c.badge === "string" && c.badge.trim()
      ? c.badge.trim()
      : typeof c.label === "string" && c.label.trim()
        ? c.label.trim()
        : null;

  const resumoCurto: string | null =
    typeof c.resumo_curto === "string" && c.resumo_curto.trim()
      ? c.resumo_curto.trim()
      : typeof c.texto_curto === "string" && c.texto_curto.trim()
        ? c.texto_curto.trim()
        : null;

  const detalhe: string | null =
    typeof c.detalhe === "string" && c.detalhe.trim() ? c.detalhe.trim() : null;

  const tag: CancelTag = normalizeCancelTag(listing);

  return { badge, resumoCurto, detalhe, tag };
}

function makeImageUrl(pathOrUrl: string | null | undefined): string {
  const FALLBACK = "/assets/img/placeholder.jpg";
  const BASE = (import.meta.env.VITE_ASSETS_BASE_URL || "").replace(/\/+$/, "");

  if (!pathOrUrl) return FALLBACK;

  const s = String(pathOrUrl).trim();
  if (!s || s === "images/placeholders/perfil-default.jpg") return FALLBACK;

  if (s.startsWith("http://") || s.startsWith("https://")) return s;

  if (s.startsWith("/")) return BASE ? `${BASE}${s}` : s;

  const rel = `/storage/${s.replace(/^\/+/, "")}`;
  return BASE ? `${BASE}${rel}` : rel;
}

function uniqueValidImages(values: unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const value of values) {
    const url = makeImageUrl(typeof value === "string" ? value : null);
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }

  return out;
}

function getSmartImageFrame(img: HTMLImageElement): ImageFrame {
  const width = img.naturalWidth || 1;
  const height = img.naturalHeight || 1;
  const ratio = height / width;

  if (ratio >= 1.15) {
    return { x: 50, y: 30 };
  }

  if (ratio >= 0.9) {
    return { x: 50, y: 40 };
  }

  return { x: 50, y: 50 };
}

export function ListingCard({ listing }: Props) {
  const [currentImage, setCurrentImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [imageFrames, setImageFrames] = useState<Record<string, ImageFrame>>({});

  const hostNome =
    (listing as any)?.anfitriao?.nome ??
    (listing as any)?.hostName ??
    (listing as any)?.title ??
    "Serviço";

  const cidade =
    (listing as any)?.anfitriao?.cidade ??
    (listing as any)?.location ??
    "";

  const tipoNome =
    (listing as any)?.tipo_servico?.nome ??
    (listing as any)?.serviceName ??
    (listing as any)?.service?.nome ??
    "";

  const limitePets =
    (listing as any)?.vagas?.limite_pets ??
    (listing as any)?.maxPets ??
    null;

  const precoDetalhado =
    (listing as any)?.precos_detalhados?.[0]?.preco != null
      ? Number((listing as any).precos_detalhados[0].preco)
      : undefined;

  const precoExibicao =
    (listing as any)?.preco_exibicao ??
    precoDetalhado ??
    (listing as any)?.preco_base ??
    0;

  const indisponivel =
    (listing as any)?.flags?.esta_disponivel === false ||
    (listing as any)?.flags?.indisponivel_manualmente === true;

  const aceita = (listing as any)?.preferencias?.aceita ?? [];
  const porte = (listing as any)?.preferencias?.porte ?? [];
  const adicionais = (listing as any)?.preferencias?.adicionais ?? [];

  const avalTotal = Number((listing as any)?.avaliacoes?.total ?? 0) || 0;
  const avalMedia = Number((listing as any)?.avaliacoes?.media ?? 0) || 0;

  const servicoId =
    (listing as any)?.servico_id ??
    (listing as any)?.id ??
    "";

  const cancel = useMemo(() => pickCancelamento(listing), [listing]);
  const cancelClass = badgeClassByTag(cancel.tag);

  const hasExplicitFocalX = typeof (listing as any)?.focal_x === "number";
  const hasExplicitFocalY = typeof (listing as any)?.focal_y === "number";

  const focalX = hasExplicitFocalX
    ? Math.max(0, Math.min(100, (listing as any).focal_x))
    : 50;

  const focalY = hasExplicitFocalY
    ? Math.max(0, Math.min(100, (listing as any).focal_y))
    : 38;

  const imagens = useMemo(() => {
    const imagensRaw = Array.isArray((listing as any)?.imagens)
      ? (listing as any).imagens
      : Array.isArray((listing as any)?.images)
        ? (listing as any).images
        : [];

    const fallbackHost =
      (listing as any)?.anfitriao?.foto ??
      (listing as any)?.anfitriao?.foto_perfil ??
      (listing as any)?.host_profile?.foto_perfil ??
      (listing as any)?.foto_perfil ??
      null;

    let normalizadas = uniqueValidImages(imagensRaw);

    if (normalizadas.length === 0 && fallbackHost) {
      normalizadas = uniqueValidImages([fallbackHost]);
    }

    return normalizadas.length > 0
      ? normalizadas
      : [makeImageUrl(null)];
  }, [listing]);

  const totalImagens = imagens.length;
  const currentImageUrl = imagens[currentImage];
  const detectedFrame = imageFrames[currentImageUrl];
  const imageFrame: ImageFrame = {
    x: hasExplicitFocalX ? focalX : detectedFrame?.x ?? focalX,
    y: hasExplicitFocalY ? focalY : detectedFrame?.y ?? focalY,
  };

  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (totalImagens <= 1) return;
    setCurrentImage((prev) => (prev + 1) % totalImagens);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (totalImagens <= 1) return;
    setCurrentImage((prev) => (prev - 1 + totalImagens) % totalImagens);
  };

  const toggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorite((v) => !v);
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (hasExplicitFocalX && hasExplicitFocalY) return;

    const img = e.currentTarget;
    const key = img.currentSrc || img.src;
    if (!key) return;

    setImageFrames((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: getSmartImageFrame(img) };
    });
  };

  return (
    <Link
      to={`/servico/${servicoId}`}
      className={cn(
        "group block rounded-xl transition-all duration-300",
        "animate-slide-up-fade opacity-0"
      )}
    >
      <div className="relative aspect-square overflow-hidden rounded-2xl bg-muted">
        <img
          src={currentImageUrl}
          alt={hostNome}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          style={{ objectPosition: `${imageFrame.x}% ${imageFrame.y}%` }}
          loading="lazy"
          decoding="async"
          onLoad={handleImageLoad}
        />

        {indisponivel && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <span className="rounded-full bg-background px-3 py-1 text-sm font-semibold">
              Indisponível
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={toggleFavorite}
          className="absolute right-3 top-3 rounded-full bg-background/80 p-2 backdrop-blur-sm"
          aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        >
          <Heart
            className={cn(
              "h-5 w-5",
              isFavorite ? "fill-coral text-coral" : "text-foreground"
            )}
          />
        </button>

        {totalImagens > 1 && (
          <>
            <button
              type="button"
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-background/90 p-1.5 shadow-sm transition-opacity md:opacity-0 md:group-hover:opacity-100"
              aria-label="Imagem anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-background/90 p-1.5 shadow-sm transition-opacity md:opacity-0 md:group-hover:opacity-100"
              aria-label="Próxima imagem"
            >
              <ChevronRight className="h-4 w-4" />
            </button>

            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1">
              {imagens.map((_, idx) => (
                <div
                  key={`${servicoId}-${idx}`}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    idx === currentImage ? "w-2.5 bg-white" : "w-1.5 bg-white/60"
                  )}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="space-y-1 pt-3">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate font-semibold">{hostNome}</p>

          {avalTotal > 0 ? (
            <div className="flex items-center gap-1 text-sm">
              <Star className="h-4 w-4 fill-black text-black" />
              <span>{avalMedia.toFixed(1)}</span>
              <span className="text-muted-foreground">({avalTotal})</span>
            </div>
          ) : (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 animate-pulse">
              Novo
            </span>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          {tipoNome || "Serviço"}
          {cidade ? ` • ${cidade}` : ""}
        </p>

        <p className="text-sm text-muted-foreground">
          Limite: {limitePets ?? "—"} pet(s)
        </p>

        <div className="flex flex-wrap gap-1 pt-1">
          {aceita.slice(0, 2).map((t: string) => (
            <span key={t} className="rounded-full bg-muted px-2 py-1 text-xs">
              {t}
            </span>
          ))}
          {porte.slice(0, 2).map((p: string) => (
            <span key={p} className="rounded-full bg-muted px-2 py-1 text-xs">
              {p}
            </span>
          ))}
          {adicionais.slice(0, 2).map((a: string) => (
            <span key={a} className="rounded-full bg-muted px-2 py-1 text-xs">
              {a}
            </span>
          ))}
        </div>

        {cancel?.badge && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">
              Cancelamento:
            </span>

            <span
              className={cn(
                "inline-flex w-fit rounded-full px-2 py-1 text-xs font-semibold",
                cancelClass
              )}
              title={cancel.detalhe || ""}
            >
              {cancel.badge}
            </span>

            {cancel.resumoCurto && (
              <span
                className="truncate text-xs text-muted-foreground"
                title={cancel.detalhe || cancel.resumoCurto}
              >
                {cancel.resumoCurto}
              </span>
            )}
          </div>
        )}

        <p className="pt-1">
          <span className="font-semibold text-foreground">
            {Number(precoExibicao || 0).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })}
          </span>
          <span className="text-muted-foreground">
            {tipoNome?.toLowerCase().includes("creche")
              ? " / período"
              : (listing as any)?.tipo_servico?.tipo_precificacao === "horaria"
                ? " / hora"
                : " / dia"}
          </span>
        </p>
      </div>
    </Link>
  );
}
